/**
 * Générateur — « Traduire un problème, une succession donnée d'opérations, un
 * programme de calcul, en une seule expression, en faisant appel ou non à des
 * parenthèses. »
 * Attendu ID : 903 | Niveau : 5ème | Domaine : Nombres et calculs / Opérations
 *
 * Mis en conformité avec la spécification 903.json (campagne 5e, 2026-09-22).
 * La spécification est la seule référence ; ce qu'elle ne dit pas est noté
 * dans le rapport audit/5eme/903.md (section « Non tranché »).
 *
 * ─── Plan de cartes ────────────────────────────────────────────────────────
 *
 *   Carte « succession » — Condenser une succession d'opérations (niveaux 1 et 2)
 *     Support « succession donnée d'opérations » : les deux (ou trois) calculs
 *     ont DÉJÀ été effectués, ligne à ligne, avec leurs résultats ; l'élève
 *     les condense en une seule expression.
 *     Formats : texte (saisie d'expression, comparaison d'expression),
 *     qcm_unique (quatre écritures).
 *     Une fois sur deux l'opération forte vient d'abord — aucune parenthèse
 *     n'est alors nécessaire (niveau 1) ; sinon la parenthèse est obligatoire
 *     (niveau 2), et une fois sur trois une troisième opération s'ajoute.
 *
 *   Carte « programme » — Traduire un programme de calcul (niveaux 3 et 5)
 *     Support « programme de calcul », le point de départ que le programme
 *     officiel nomme. Le nombre de départ est DONNÉ (jamais « choisir un
 *     nombre » : sans cela deux écritures correctes coexistent, cf.
 *     points_de_vigilance de la spec).
 *     Formats : texte (2 ou 3 étapes, niveau 3), qcm_unique (2 ou 3 étapes,
 *     niveau 3), texte long (4 étapes, niveau 5).
 *     Les parenthèses sont posées par la règle des priorités, jamais en trop.
 *
 *   Carte « lire_expression » — Quel programme cette expression décrit-elle ?
 *     (niveau 6) Sens inverse de la traduction, minoritaire dans une série
 *     (un format sur douze). Format : qcm_unique (quatre programmes).
 *
 *   Carte « salaire » — Traduire et calculer un salaire net (niveau 4)
 *     Problème en contexte à deux étapes, grandeurs décimales, une parenthèse
 *     nécessaire. Volume horaire MENSUEL ENTIER → h × (s − r) exact au centime
 *     par construction.
 *     Formats : qcm_unique (choisir l'expression), texte (effectuer).
 *
 *   Carte « somme_produits » — Traduire une somme de produits (niveau 4)
 *     Problème en contexte SANS aucune parenthèse (c'est ce qui empêche le
 *     réflexe de parenthéser). Deux termes, branche entière ou branche
 *     décimale. Les produits d'étapes sont FORCÉS DISTINCTS (chaque trou doit
 *     tester SON produit).
 *     Formats : qcm_unique (expression), texte_trou_saisie (calcul décomposé).
 *
 *   Carte « rebours » — Traduire un problème à rebours (niveau 4)
 *     Problème en contexte, construction à rebours : le prix de l'objet
 *     inconnu est tiré D'ABORD, le total s'en déduit → toutes les étapes
 *     exactes.
 *     Formats : qcm_unique (expression de la dépense connue), texte_trou_saisie.
 *
 * ─── Spécification (903.json) ──────────────────────────────────────────────
 *
 *   Bornes appliquées :
 *     - supports : les TROIS que le programme nomme sont produits (problème en
 *       contexte, succession donnée d'opérations, programme de calcul) ;
 *     - nombre d'opérations de l'expression attendue : 2 à 4 ;
 *     - nombre d'étapes d'un programme de calcul : 2 à 4 ;
 *     - opérandes d'une succession ou d'un programme : entiers de 2 à 20 ;
 *     - nombres écrits dans une expression (dividendes et résultats
 *       intermédiaires compris) : de 2 à 400 ;
 *     - nombres d'une situation en contexte : de 0,05 à 400, décimaux à deux
 *       décimales au plus ;
 *     - part des questions sans aucune parenthèse : 4,5 formats sur 12,
 *       soit 0,375 (la spec demande entre 0,33 et 0,5) ;
 *     - résultat intermédiaire : entier, au moins 2.
 *   Exclusions appliquées : aucune division non exacte ; aucun résultat
 *     intermédiaire nul ni négatif ; aucun facteur ni diviseur égal à 1 ;
 *     aucune expression réductible à une seule opération (2 opérations au
 *     minimum) ; aucune parenthèse inutile dans l'expression attendue ; aucune
 *     tâche qui va d'un nombre vers une écriture de ce nombre.
 *   Erreurs visées → mots-clés (codes des distracteurs
 *     `_meta.params.distracteurs[].code`, ou `_meta.params.erreurVisee`
 *     pour une erreur provoquée en saisie) :
 *     1. « Écrire une chaîne d'égalités fausse… »            → chaine_egalites
 *     2. « Recopier les résultats intermédiaires… »          → resultat_recopie
 *     3. « Omettre la parenthèse qui rend l'écriture fidèle » → parenthese_omise
 *     4. « Parenthéser systématiquement… »                   → parenthese_systematique
 *     5. « Traduire un programme en une suite de lignes… »   → lignes_au_lieu_d_une_expression
 *     6. « Inverser l'ordre des termes d'une soustraction… » → ordre_inverse
 *     7. « Croire qu'une seule écriture d'un quotient est correcte… »
 *                                                            → ecriture_unique_du_quotient
 *     Deux distracteurs de problème en contexte ne matérialisent aucune de ces
 *     sept erreurs mais restent des erreurs d'élève attestées : ils portent les
 *     codes HORS TABLE `grandeurs_confondues` (une grandeur est appliquée à une
 *     autre : (h − r) × s, produits croisés) et `operation_confondue` (tout
 *     additionner, ou une opération remplacée par une autre).
 *   Graduation → cartes :
 *     niveau 1 → succession_texte, succession_qcm_unique (branche sans parenthèse) ;
 *     niveau 2 → succession_texte, succession_qcm_unique (branche avec parenthèse) ;
 *     niveau 3 → programme_texte, programme_qcm_unique ;
 *     niveau 4 → salaire (2 formats), somme_produits (2 formats), rebours (2 formats) ;
 *     niveau 5 → programme_long_texte (4 étapes) ;
 *     niveau 6 → lire_expression_qcm_unique.
 *   Réglages annoncés dans la consigne (la spec impose de décider ET
 *   d'annoncer, elle ne dit pas dans quel sens — voir « Non tranché ») :
 *     - parenthèses superflues REFUSÉES (« n'écris que les parenthèses
 *       nécessaires ») ;
 *     - ordre de l'énoncé IMPOSÉ, donc ordre des facteurs d'un produit NON
 *       libre (« garde l'ordre de l'énoncé ») ;
 *     - le signe ÷ peut s'écrire « / » ou « : », le signe × « x » ou « * »
 *       (c'est l'erreur visée 7 qui est ainsi désamorcée).
 *
 * ─── Conventions transverses ───────────────────────────────────────────────
 *   - Les expressions sont du TEXTE BRUT dans les choix de QCM et les réponses
 *     de saisie (l'élève les tape au clavier) ; les expressions AFFICHÉES dans
 *     un énoncé passent par KaTeX (`exprTex`).
 *   - Le drapeau calculatrice vit dans `payload.contenu` (`_meta` est strippé
 *     au POST). Accordé aux saisies décimales de problèmes en contexte, refusé
 *     partout où c'est l'ÉCRITURE qui est demandée (on n'y calcule rien).
 *   - Coups de pouce : `src/generators/coupsDePouce/903.js`, les six formats
 *     de problème en contexte. Les cartes « succession », « programme » et
 *     « lire_expression » n'en ont pas encore.
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 903;

const L_SUCC = 'Condenser une succession d\'opérations';
const L_PROG = 'Traduire un programme de calcul';
const L_LIRE = 'Quel programme cette expression décrit-elle ?';
const L_SAL  = 'Traduire et calculer un salaire net';
const L_SP   = 'Traduire une somme de produits';
const L_REB  = 'Traduire un problème à rebours';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Espace insécable : jamais un nombre séparé de son unité en fin de ligne. */
const NB = ' ';

/** Paramètres de tirage exposés au test de conformité (jamais persistés). */
const avecParams = (payload, params) => {
    payload._meta.params = params;
    return payload;
};

/** Calculatrice autorisée — dans `contenu`, seul canal qui survit au POST. */
const avecCalculatrice = (payload) => {
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

/** Centimes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

/**
 * Centimes → montant en euros, DEUX décimales fixes (« 11,40 »).
 * Réservé aux prix et aux salaires : sur un montant, « 11,4 € » se lit comme
 * une coquille (revue 2026-08-02). Les volumes en m³ gardent `centToFr`.
 */
const euroFr = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;

/** Écriture française → nombre JS (pour `_meta.params` uniquement). */
const valeur = (s) => Number(String(s).replace(',', '.'));

// ─── Expressions : affichage et écritures admises ────────────

/** Expression en texte brut → KaTeX (affichage d'énoncé uniquement). */
const exprTex = (s) => tex(String(s)
    .replace(/×/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/−/g, '-'));

/**
 * Les écritures admises d'une expression, pour une saisie en comparaison
 * d'expression. `normaliserExpression` retire déjà les espaces, ramène « × »
 * et « * » l'un sur l'autre et « − » sur « - » : il ne reste à énumérer que
 * les synonymes que le clavier impose, « x » pour ×, « / » et « : » pour ÷.
 * Liste FINIE et auditable : ni l'ordre des termes ni les parenthèses ne
 * varient (la consigne les impose).
 */
const SYNONYMES_SIGNE = { '×': ['×', ' x '], '÷': ['÷', ' / ', ' : '] };

const ecrituresExpr = (expr) => {
    let ecritures = [''];
    String(expr).split(/([×÷])/).forEach((morceau) => {
        const syn = SYNONYMES_SIGNE[morceau];
        ecritures = syn
            ? ecritures.flatMap((debut) => syn.map((s) => debut + s))
            : ecritures.map((debut) => debut + morceau);
    });
    return [...new Set(ecritures)];
};

/** La consigne d'écriture, identique partout : les trois réglages annoncés. */
const REGLES_ECRITURE =
    '<p>Garde l\'ordre de l\'énoncé et n\'écris que les parenthèses nécessaires. ' +
    'Le signe × peut se taper « x » ou « * », le signe ÷ peut se taper « / ». ' +
    'Exemple d\'écriture : (30 + 7) x 2</p>';

const OPS_FORTES = ['×', '÷'];
const OPS_FAIBLES = ['+', '−'];

/**
 * Assemble la chaîne d'étapes en UNE expression, avec les parenthèses
 * strictement nécessaires : une opération forte qui suit une opération faible
 * force la parenthèse, jamais dans l'autre sens.
 */
const assembler = (depart, etapes) => {
    let e = String(depart);
    let faible = false;
    etapes.forEach(({ op, k }) => {
        if (op === '×' || op === '÷') {
            e = faible ? `(${e}) ${op} ${k}` : `${e} ${op} ${k}`;
            faible = false;
        } else {
            e = `${e} ${op} ${k}`;
            faible = true;
        }
    });
    return e;
};

/**
 * La valeur de la même suite d'opérations écrite SANS aucune parenthèse, en appliquant les
 * priorités. Sert la garde ci-dessous : la spécification exclut toute parenthèse inutile, or une
 * parenthèse peut être neutralisée par la suite des opérations (« (11 − 7) × 5 ÷ 5 » vaut autant
 * que « 11 − 7 × 5 ÷ 5 », les deux étapes fortes s'annulant). Le programme serait alors vide de
 * sens et l'élève qui omet la parenthèse trouverait le bon nombre.
 */
const valeurSansParentheses = (depart, etapes) => {
    const termes = [depart];      // termes additifs, à sommer à la fin
    const signes = [1];
    etapes.forEach(({ op, k }) => {
        if (op === '×') termes[termes.length - 1] *= k;
        else if (op === '÷') termes[termes.length - 1] /= k;
        else { termes.push(k); signes.push(op === '+' ? 1 : -1); }
    });
    return termes.reduce((s, t, i) => s + signes[i] * t, 0);
};

/** Les diviseurs de `v` entre 2 et 20 qui laissent un quotient d'au moins 2. */
const diviseursUtiles = (v) => {
    const out = [];
    for (let d = 2; d <= 20; d++) if (v % d === 0 && v / d >= 2) out.push(d);
    return out;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Condenser une succession d'opérations (niveaux 1 et 2)
// ═══════════════════════════════════════════════════════════

/**
 * Branche SANS parenthèse (niveau 1) : l'opération forte vient d'abord, la
 * priorité suffit. `a × b + c`, `a ÷ b − c`…
 */
const tenterSuccessionSimple = () => {
    const op1 = randomChoice(OPS_FORTES);
    let a;
    let b;
    let produit;
    if (op1 === '×') {
        a = randInt(2, 20);
        b = randInt(2, 20);
        produit = a * b;
    } else {
        b = randInt(2, 20);
        const q = randInt(2, 20);
        a = b * q;
        produit = q;
    }
    if (a > 400 || produit < 2) return null;
    const op2 = randomChoice(OPS_FAIBLES);
    let c;
    let resultat;
    if (op2 === '+') {
        c = randInt(2, 20);
        resultat = produit + c;
    } else {
        if (produit < 4) return null;
        c = randInt(2, Math.min(20, produit - 2));
        resultat = produit - c;
    }
    if (resultat < 2 || resultat > 400) return null;
    const expression = `${a} ${op1} ${b} ${op2} ${c}`;
    const extras = [{ valeur: `${a} ${op1} ${b} = ${produit} ${op2} ${c}`, code: 'chaine_egalites' }];
    if (op2 === '−') extras.push({ valeur: `${c} − ${a} ${op1} ${b}`, code: 'ordre_inverse' });
    if (op1 === '÷') extras.push({ valeur: `${b} ÷ ${a} ${op2} ${c}`, code: 'ordre_inverse' });
    return {
        niveau: 1,
        sansParenthese: true,
        operations: 2,
        expression,
        resultat,
        lignes: [`${a} ${op1} ${b} = ${produit}`, `${produit} ${op2} ${c} = ${resultat}`],
        operandes: op1 === '÷' ? [b, a / b, c] : [a, b, c],
        affiches: [a, b, c, produit, resultat],
        intermediaires: [produit],
        divise: op1 === '÷',
        pieges: [
            { valeur: `(${a} ${op1} ${b}) ${op2} ${c}`, code: 'parenthese_systematique' },
            { valeur: `${produit} ${op2} ${c}`, code: 'resultat_recopie' },
            randomChoice(extras),
        ],
    };
};

/**
 * Branche AVEC parenthèse (niveau 2) : l'opération faible vient d'abord, la
 * parenthèse est obligatoire. Une fois sur trois, une troisième opération
 * s'ajoute (la spec autorise deux ou trois opérations à ce niveau).
 */
const tenterSuccessionParenthese = () => {
    const op1 = randomChoice(OPS_FAIBLES);
    let a;
    let b;
    let somme;
    if (op1 === '+') {
        a = randInt(2, 20);
        b = randInt(2, 20);
        somme = a + b;
    } else {
        a = randInt(4, 20);
        b = randInt(2, a - 2);
        somme = a - b;
    }
    if (somme < 2) return null;
    const op2 = randomChoice(OPS_FORTES);
    let c;
    let resultat;
    if (op2 === '×') {
        c = randInt(2, 20);
        resultat = somme * c;
        if (resultat > 400) return null;
    } else {
        const divs = diviseursUtiles(somme);
        if (!divs.length) return null;
        c = randomChoice(divs);
        resultat = somme / c;
    }
    if (resultat < 2) return null;

    const base = `(${a} ${op1} ${b}) ${op2} ${c}`;
    const sansParen = `${a} ${op1} ${b} ${op2} ${c}`;
    const lignes = [`${a} ${op1} ${b} = ${somme}`, `${somme} ${op2} ${c} = ${resultat}`];
    const ordreInverse = [];
    if (op2 === '÷') ordreInverse.push(`${c} ÷ (${a} ${op1} ${b})`);
    if (op1 === '−') ordreInverse.push(`(${b} − ${a}) ${op2} ${c}`);

    if (!randomChoice([false, false, true])) {
        const extras = [{ valeur: `${a} ${op1} ${b} = ${somme} ${op2} ${c}`, code: 'chaine_egalites' }];
        ordreInverse.forEach((v) => extras.push({ valeur: v, code: 'ordre_inverse' }));
        return {
            niveau: 2,
            sansParenthese: false,
            operations: 2,
            expression: base,
            resultat,
            lignes,
            operandes: op2 === '÷' ? [a, b, c] : [a, b, c],
            affiches: [a, b, c, somme, resultat],
            intermediaires: [somme],
            divise: op2 === '÷',
            pieges: [
                { valeur: sansParen, code: 'parenthese_omise' },
                { valeur: `${somme} ${op2} ${c}`, code: 'resultat_recopie' },
                randomChoice(extras),
            ],
        };
    }

    // Troisième opération : une addition ou une soustraction après le produit.
    const op3 = randomChoice(OPS_FAIBLES);
    let d;
    let resultat3;
    if (op3 === '+') {
        d = randInt(2, 20);
        resultat3 = resultat + d;
    } else {
        if (resultat < 4) return null;
        d = randInt(2, Math.min(20, resultat - 2));
        resultat3 = resultat - d;
    }
    if (resultat3 < 2 || resultat3 > 400) return null;
    const extras3 = [{ valeur: `${base} = ${resultat} ${op3} ${d}`, code: 'chaine_egalites' }];
    ordreInverse.forEach((v) => extras3.push({ valeur: `${v} ${op3} ${d}`, code: 'ordre_inverse' }));
    return {
        niveau: 2,
        sansParenthese: false,
        operations: 3,
        expression: `${base} ${op3} ${d}`,
        resultat: resultat3,
        lignes: [...lignes, `${resultat} ${op3} ${d} = ${resultat3}`],
        operandes: [a, b, c, d],
        affiches: [a, b, c, d, somme, resultat, resultat3],
        intermediaires: [somme, resultat],
        divise: op2 === '÷',
        pieges: [
            { valeur: `${sansParen} ${op3} ${d}`, code: 'parenthese_omise' },
            { valeur: `${resultat} ${op3} ${d}`, code: 'resultat_recopie' },
            randomChoice(extras3),
        ],
    };
};

/** Replis déterministes, vérifiés : jamais de `throw` au tirage. */
const REPLI_SUCCESSION_SIMPLE = {
    niveau: 1, sansParenthese: true, operations: 2,
    expression: '7 × 6 + 13', resultat: 55,
    lignes: ['7 × 6 = 42', '42 + 13 = 55'],
    operandes: [7, 6, 13], affiches: [7, 6, 13, 42, 55], intermediaires: [42], divise: false,
    pieges: [
        { valeur: '(7 × 6) + 13', code: 'parenthese_systematique' },
        { valeur: '42 + 13', code: 'resultat_recopie' },
        { valeur: '7 × 6 = 42 + 13', code: 'chaine_egalites' },
    ],
};

const REPLI_SUCCESSION_PAREN = {
    niveau: 2, sansParenthese: false, operations: 2,
    expression: '(12 + 7) × 4', resultat: 76,
    lignes: ['12 + 7 = 19', '19 × 4 = 76'],
    operandes: [12, 7, 4], affiches: [12, 7, 4, 19, 76], intermediaires: [19], divise: false,
    pieges: [
        { valeur: '12 + 7 × 4', code: 'parenthese_omise' },
        { valeur: '19 × 4', code: 'resultat_recopie' },
        { valeur: '12 + 7 = 19 × 4', code: 'chaine_egalites' },
    ],
};

const tirerSuccession = (avecParenthese) => {
    for (let essai = 0; essai < 80; essai++) {
        const t = avecParenthese ? tenterSuccessionParenthese() : tenterSuccessionSimple();
        if (t) return t;
    }
    return avecParenthese ? REPLI_SUCCESSION_PAREN : REPLI_SUCCESSION_SIMPLE;
};

const enonceSuccession = (t) =>
    `<p>${t.lignes.length === 2 ? 'Deux calculs ont été effectués' : 'Trois calculs ont été effectués'} ` +
    'l\'un après l\'autre :</p>' +
    `<p>${t.lignes.map(exprTex).join('<br>')}</p>`;

const paramsSuccession = (t, extra = {}) => ({
    niveau: t.niveau,
    support: 'succession',
    sansParenthese: t.sansParenthese,
    operations: t.operations,
    operandes: t.operandes,
    affiches: t.affiches,
    intermediaires: t.intermediaires,
    expression: t.expression,
    ...extra,
});

const genSuccessionSaisie = () => {
    const t = tirerSuccession(randomChoice([true, false]));
    const ecritures = ecrituresExpr(t.expression);
    const texte = enonceSuccession(t) +
        `<p>Écris en <strong>une seule expression</strong> le calcul qui donne ${t.resultat}, ` +
        'sans le calculer à nouveau.</p>' + REGLES_ECRITURE;
    const payload = makeSaisie(t.expression, texte, {
        ...opts(L_SUCC), comparaison: 'expression', reponses_acceptees: ecritures.slice(1),
    });
    return avecParams(payload, paramsSuccession(t, {
        ...(t.divise ? { erreurVisee: 'ecriture_unique_du_quotient' } : {}),
    }));
};

const genSuccessionQcm = () => {
    const t = tirerSuccession(randomChoice([true, false]));
    const choix = [t.expression, ...t.pieges.map((p) => p.valeur)];
    const texte = enonceSuccession(t) +
        '<p>Quelle écriture donne ce calcul en <strong>une seule expression</strong> ?</p>';
    const payload = makeQcm(choix, t.expression, texte, opts(L_SUCC));
    return avecParams(payload, paramsSuccession(t, { distracteurs: t.pieges }));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Traduire un programme de calcul (niveaux 3 et 5)
// ═══════════════════════════════════════════════════════════

const VERBES = {
    '+': (k) => `Ajouter ${k}`,
    '−': (k) => `Soustraire ${k}`,
    '×': (k) => `Multiplier par ${k}`,
    '÷': (k) => `Diviser par ${k}`,
};

const VERBES_MIN = {
    '+': (k) => `ajouter ${k}`,
    '−': (k) => `soustraire ${k}`,
    '×': (k) => `multiplier par ${k}`,
    '÷': (k) => `diviser par ${k}`,
};

/**
 * Un programme de calcul de `nbEtapes` étapes, dont l'expression comporte ou
 * non une parenthèse. Le choix des opérations est CONTRAINT pour que le
 * résultat soit atteint sans boucle longue : sans parenthèse, plus aucune
 * opération forte après une opération faible ; avec parenthèse, une opération
 * faible puis une opération forte ouvrent la marche.
 */
const tenterProgramme = (nbEtapes, sansParenthese) => {
    const x0 = randInt(2, 20);
    let val = x0;
    let faible = false;
    const etapes = [];
    const operandes = [x0];
    const intermediaires = [];
    for (let i = 0; i < nbEtapes; i++) {
        let permises;
        if (sansParenthese) permises = faible ? OPS_FAIBLES : [...OPS_FAIBLES, ...OPS_FORTES];
        else if (i === 0) permises = OPS_FAIBLES;
        else if (i === 1) permises = OPS_FORTES;
        else permises = [...OPS_FAIBLES, ...OPS_FORTES];
        const op = randomChoice(permises);
        let k;
        if (op === '+') k = randInt(2, 20);
        else if (op === '−') {
            if (val < 4) return null;
            k = randInt(2, Math.min(20, val - 2));
        } else if (op === '×') k = randInt(2, 20);
        else {
            const divs = diviseursUtiles(val);
            if (!divs.length) return null;
            k = randomChoice(divs);
        }
        const suivant = op === '+' ? val + k : op === '−' ? val - k : op === '×' ? val * k : val / k;
        if (suivant < 2 || suivant > 400) return null;
        if (op === '×' || op === '÷') faible = false; else faible = true;
        etapes.push({ op, k });
        operandes.push(k);
        val = suivant;
        intermediaires.push(val);
    }
    const expression = assembler(x0, etapes);
    if (expression.includes('(') === sansParenthese) return null;
    // Exclusion de la spec : aucune parenthèse inutile. Si l'écriture plate donne la même valeur,
    // la parenthèse ne sert à rien et le programme est dégénéré : on rejoue le tirage.
    if (expression.includes('(') && Math.abs(valeurSansParentheses(x0, etapes) - val) < 1e-9) return null;
    return {
        x0, etapes, expression, resultat: val, operandes,
        affiches: [x0, ...etapes.map((e) => e.k)],
        intermediaires,
        sansParenthese,
        operations: nbEtapes,
        divise: etapes.some((e) => e.op === '÷'),
    };
};

const REPLI_PROGRAMME = {
    x0: 7,
    etapes: [{ op: '+', k: 5 }, { op: '×', k: 3 }],
    expression: '(7 + 5) × 3', resultat: 36,
    operandes: [7, 5, 3], affiches: [7, 5, 3], intermediaires: [12, 36],
    sansParenthese: false, operations: 2, divise: false,
};

const REPLI_PROGRAMME_LONG = {
    x0: 7,
    etapes: [{ op: '+', k: 5 }, { op: '×', k: 3 }, { op: '−', k: 6 }, { op: '÷', k: 5 }],
    expression: '((7 + 5) × 3 − 6) ÷ 5', resultat: 6,
    operandes: [7, 5, 3, 6, 5], affiches: [7, 5, 3, 6, 5], intermediaires: [12, 36, 30, 6],
    sansParenthese: false, operations: 4, divise: true,
};

const tirerProgramme = (nbEtapes, sansParenthese) => {
    for (let essai = 0; essai < 120; essai++) {
        const t = tenterProgramme(nbEtapes, sansParenthese);
        if (t) return t;
    }
    return nbEtapes >= 4 ? REPLI_PROGRAMME_LONG : REPLI_PROGRAMME;
};

const enonceProgramme = (t) =>
    '<p><strong>Programme de calcul</strong></p><p>' +
    [`Choisir le nombre <strong>${t.x0}</strong>`, ...t.etapes.map((e) => VERBES[e.op](`<strong>${e.k}</strong>`))]
        .map((l) => `• ${l}.`).join('<br>') +
    '</p>';

const paramsProgramme = (t, niveau, extra = {}) => ({
    niveau,
    support: 'programme',
    sansParenthese: t.sansParenthese,
    operations: t.operations,
    etapes: t.etapes.length,
    operandes: t.operandes,
    affiches: t.affiches,
    intermediaires: t.intermediaires,
    expression: t.expression,
    ...extra,
});

const consigneProgramme =
    '<p>Écris en <strong>une seule expression</strong> le calcul de ce programme, ' +
    'sans le calculer.</p>';

const genProgrammeSaisie = () => {
    const t = tirerProgramme(randomChoice([2, 3]), randomChoice([true, false]));
    const ecritures = ecrituresExpr(t.expression);
    const payload = makeSaisie(t.expression, enonceProgramme(t) + consigneProgramme + REGLES_ECRITURE, {
        ...opts(L_PROG), comparaison: 'expression', reponses_acceptees: ecritures.slice(1),
    });
    return avecParams(payload, paramsProgramme(t, 3, {
        ...(t.divise ? { erreurVisee: 'ecriture_unique_du_quotient' } : {}),
    }));
};

const genProgrammeLongSaisie = () => {
    const t = tirerProgramme(4, false);
    const ecritures = ecrituresExpr(t.expression);
    const payload = makeSaisie(t.expression, enonceProgramme(t) + consigneProgramme + REGLES_ECRITURE, {
        ...opts(L_PROG), comparaison: 'expression', reponses_acceptees: ecritures.slice(1),
    });
    return avecParams(payload, paramsProgramme(t, 5, {
        ...(t.divise ? { erreurVisee: 'ecriture_unique_du_quotient' } : {}),
    }));
};

const genProgrammeQcm = () => {
    const t = tirerProgramme(randomChoice([2, 3]), randomChoice([true, false]));
    const premiere = t.etapes[0];
    const suite = assembler(t.intermediaires[0], t.etapes.slice(1));
    const debut = `${t.x0} ${premiere.op} ${premiere.k}`;
    const pieges = [
        t.sansParenthese
            ? { valeur: assembler(`(${debut})`, t.etapes.slice(1)), code: 'parenthese_systematique' }
            : { valeur: t.expression.replace(/[()]/g, ''), code: 'parenthese_omise' },
        { valeur: suite, code: 'resultat_recopie' },
        randomChoice([
            { valeur: `${debut} = ${suite}`, code: 'chaine_egalites' },
            { valeur: `${debut} puis ${suite}`, code: 'lignes_au_lieu_d_une_expression' },
        ]),
    ];
    const choix = [t.expression, ...pieges.map((p) => p.valeur)];
    const texte = enonceProgramme(t) +
        '<p>Quelle écriture traduit ce programme en <strong>une seule expression</strong> ?</p>';
    const payload = makeQcm(choix, t.expression, texte, opts(L_PROG));
    return avecParams(payload, paramsProgramme(t, 3, { distracteurs: pieges }));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Quel programme cette expression décrit-elle ? (niveau 6)
// ═══════════════════════════════════════════════════════════

/**
 * Sens inverse de la traduction. Les quatre programmes proposés décrivent
 * quatre expressions STRUCTURELLEMENT différentes : la bonne, celle qu'on
 * obtient en oubliant (ou en ajoutant) la parenthèse, celle où les deux
 * étapes sont interverties, celle où la seconde opération est remplacée.
 */
const tenterLireExpression = (sansParenthese) => {
    const x0 = randInt(2, 20);
    const k1 = randInt(2, 20);
    if (k1 === x0) return null;
    if (sansParenthese) {
        if (x0 * k1 > 400) return null;
        const op2 = randomChoice(OPS_FAIBLES);
        let k2;
        if (op2 === '+') k2 = randInt(2, 20);
        else {
            if (x0 * k1 < 4) return null;
            k2 = randInt(2, Math.min(20, x0 * k1 - 2));
        }
        if (k2 === k1) return null;
        const expression = `${x0} × ${k1} ${op2} ${k2}`;
        return {
            expression, sansParenthese: true, operations: 2,
            operandes: [x0, k1, k2], affiches: [x0, k1, k2],
            bonne: `Choisir ${x0}, multiplier par ${k1}, ${VERBES_MIN[op2](k2)}`,
            pieges: [
                {
                    valeur: `Choisir ${k1}, ${VERBES_MIN[op2](k2)}, multiplier par ${x0}`,
                    code: 'parenthese_systematique',
                },
                {
                    valeur: `Choisir ${x0}, ${VERBES_MIN[op2](k2)}, multiplier par ${k1}`,
                    code: 'ordre_inverse',
                },
                {
                    valeur: `Choisir ${x0}, multiplier par ${k1}, multiplier par ${k2}`,
                    code: 'operation_confondue',
                },
            ],
        };
    }
    const somme = x0 + k1;
    const op2 = randomChoice(OPS_FORTES);
    let k2;
    if (op2 === '×') {
        k2 = randInt(2, 20);
        if (somme * k2 > 400) return null;
    } else {
        const divs = diviseursUtiles(somme);
        if (!divs.length) return null;
        k2 = randomChoice(divs);
    }
    if (k2 === k1 || k2 === x0) return null;
    const expression = `(${x0} + ${k1}) ${op2} ${k2}`;
    return {
        expression, sansParenthese: false, operations: 2,
        operandes: [x0, k1, k2], affiches: [x0, k1, k2],
        bonne: `Choisir ${x0}, ajouter ${k1}, ${VERBES_MIN[op2](k2)}`,
        pieges: [
            {
                valeur: `Choisir ${k1}, ${VERBES_MIN[op2](k2)}, ajouter ${x0}`,
                code: 'parenthese_omise',
            },
            {
                valeur: `Choisir ${x0}, ${VERBES_MIN[op2](k2)}, ajouter ${k1}`,
                code: 'ordre_inverse',
            },
            {
                valeur: `Choisir ${x0}, ajouter ${k1}, ajouter ${k2}`,
                code: 'operation_confondue',
            },
        ],
    };
};

const REPLI_LIRE = {
    expression: '(9 + 4) × 5', sansParenthese: false, operations: 2,
    operandes: [9, 4, 5], affiches: [9, 4, 5],
    bonne: 'Choisir 9, ajouter 4, multiplier par 5',
    pieges: [
        { valeur: 'Choisir 4, multiplier par 5, ajouter 9', code: 'parenthese_omise' },
        { valeur: 'Choisir 9, multiplier par 5, ajouter 4', code: 'ordre_inverse' },
        { valeur: 'Choisir 9, ajouter 4, ajouter 5', code: 'operation_confondue' },
    ],
};

const genLireExpressionQcm = () => {
    const vise = randomChoice([true, false]);
    let t = null;
    for (let essai = 0; essai < 80 && !t; essai++) t = tenterLireExpression(vise);
    if (!t) t = REPLI_LIRE;
    const choix = [t.bonne, ...t.pieges.map((p) => p.valeur)];
    const texte = `<p>Voici une expression : ${exprTex(t.expression)}</p>` +
        '<p>Quel programme de calcul cette expression décrit-elle ?</p>';
    const payload = makeQcm(choix, t.bonne, texte, opts(L_LIRE));
    return avecParams(payload, {
        niveau: 6,
        support: 'expression',
        sansParenthese: t.sansParenthese,
        operations: t.operations,
        operandes: t.operandes,
        affiches: t.affiches,
        intermediaires: [],
        expression: t.expression,
        distracteurs: t.pieges,
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Le salaire net (niveau 4)
// ═══════════════════════════════════════════════════════════

const CTX_SALAIRE = [
    { qui: 'Une ouvrière' },
    { qui: 'Un technicien' },
    { qui: 'Une infirmière' },
    { qui: 'Un magasinier' },
];

const tirerSalaire = () => {
    const h = randomChoice([140, 144, 148, 152, 156, 160]);
    const s = randInt(12, 16);                            // salaire horaire brut (entier)
    // Retenue tirée dans une PLAGE et non dans 4 valeurs : le vivier de la carte
    // passe de 480 à 24 120 énoncés (⚠️ doublons au harnais dans la majorité des
    // runs). Difficulté inchangée — une soustraction décimale reste une
    // soustraction décimale (revue 2026-08-02).
    const rC = randInt(150, 350);                         // retenue horaire (centimes)
    const netC = s * 100 - rC;                            // net horaire (centimes)
    return { ctx: randomChoice(CTX_SALAIRE), h, s, rC, rF: euroFr(rC), netC, totalC: h * netC };
};

const enonceSalaire = ({ ctx, h, s, rF }) =>
    `<p>${ctx.qui} travaille <strong>${h} heures par mois</strong>. ` +
    `Son salaire horaire est de <strong>${s}${NB}€</strong>. On retient sur ce salaire horaire ` +
    `<strong>${rF}${NB}€</strong> de cotisations sociales.</p>`;

const paramsSalaire = (t, extra = {}) => ({
    niveau: 4,
    support: 'contexte',
    sansParenthese: false,
    operations: 2,
    contexte: [t.h, t.s, valeur(t.rF)],
    intermediaires: [t.netC / 100],
    // Le RÉSULTAT (environ 1 500 €) n'est pas une donnée de la situation : la
    // spec borne les nombres tirés et écrits, pas ce que le calcul produit.
    resultat: t.totalC / 100,
    expression: `${t.h} × (${t.s} − ${t.rF})`,
    ...extra,
});

const genSalaireQcm = () => {
    const t = tirerSalaire();
    const { h, s, rF } = t;
    const bonne = `${h} × (${s} − ${rF})`;
    const pieges = [
        // La parenthèse manque : la retenue n'est enlevée qu'une seule fois.
        { valeur: `${h} × ${s} − ${rF}`, code: 'parenthese_omise' },
        // L'ordre des termes de la soustraction est inversé.
        { valeur: `${h} × (${rF} − ${s})`, code: 'ordre_inverse' },
        // La retenue est appliquée aux heures, pas au salaire horaire.
        { valeur: `(${h} − ${rF}) × ${s}`, code: 'grandeurs_confondues' },
    ];
    const texte = enonceSalaire(t) +
        '<p>Quelle expression permet de calculer le <strong>salaire mensuel net</strong> ' +
        '(après retenue des cotisations) ?</p>';
    const payload = makeQcm([bonne, ...pieges.map((p) => p.valeur)], bonne, texte, opts(L_SAL));
    return avecParams(payload, paramsSalaire(t, { distracteurs: pieges }));
};

const genSalaireSaisie = () => {
    const t = tirerSalaire();
    const texte = enonceSalaire(t) +
        '<p>Calcule le salaire mensuel net. Écris ta réponse en écriture décimale ' +
        '(ex. : 1521,52).</p>';
    const payload = makeSaisie(euroFr(t.totalC), texte, { ...opts(L_SAL), suffixe: '€' });
    // Calculatrice AUTORISÉE (revue 2026-08-02) : l'attendu porte sur la mise en
    // expression, pas sur la multiplication décimale posée (148 × 10,01).
    return avecParams(avecCalculatrice(payload), paramsSalaire(t));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Somme de produits (niveau 4, aucune parenthèse)
// ═══════════════════════════════════════════════════════════

// `ch` accorde « contenant chacun / chacune » avec le contenant (c1 et c2 sont
// toujours le même mot dans un contexte donné) — revue 2026-08-02.
const CTX_SP2 = [
    { c1: 'sacs', o1: 'bouteilles en plastique', c2: 'sacs', o2: 'canettes en aluminium', ch: 'chacun', quoi: 'déchets ramassés' },
    { c1: 'caisses', o1: 'pommes', c2: 'caisses', o2: 'poires', ch: 'chacune', quoi: 'fruits récoltés' },
    { c1: 'boîtes', o1: 'crayons', c2: 'boîtes', o2: 'feutres', ch: 'chacune', quoi: 'objets distribués' },
];

/**
 * Deux termes entiers — produits FORCÉS distincts (chaque trou teste SON
 * produit). Bornes : le total reste sous 400 (spec, nombres écrits dans une
 * expression), d'où un contenu par contenant plafonné à 16.
 */
const tirerSP2 = () => {
    for (let i = 0; i < 40; i++) {
        const n1 = randInt(4, 12);
        const p1 = randInt(8, 16);
        const n2 = randInt(4, 12);
        const p2 = randInt(8, 16);
        if (n1 === n2 || p1 === p2) continue;
        const P1 = n1 * p1;
        const P2 = n2 * p2;
        if (P1 === P2) continue;
        const croise = n1 * p2 + n2 * p1;
        if (croise === P1 + P2) continue;               // le piège croisé doit être FAUX
        return { n1, p1, n2, p2, P1, P2, T: P1 + P2, croise, ctx: randomChoice(CTX_SP2) };
    }
    return { n1: 6, p1: 15, n2: 9, p2: 11, P1: 90, P2: 99, T: 189, croise: 6 * 11 + 9 * 15, ctx: CTX_SP2[0] };
};

/**
 * Deux termes décimaux (zone humide) — volumes en centièmes, produits
 * distincts. Deux termes et non trois : trois termes feraient CINQ opérations,
 * au-delà du maximum de quatre que la spec fixe pour une expression.
 */
const tirerSP2dec = () => {
    for (let i = 0; i < 40; i++) {
        const ns = [randInt(2, 6), randInt(2, 6)];
        const vs = [randInt(105, 395), randInt(105, 395)]
            .map((v) => (v % 5 === 0 ? v + randomChoice([1, 2, 3]) : v));   // centièmes, pas ronds
        if (ns[0] === ns[1] || vs[0] === vs[1]) continue;
        const Ps = ns.map((n, i2) => n * vs[i2]);
        if (Ps[0] === Ps[1]) continue;
        const T = Ps[0] + Ps[1];
        // Le piège « tout additionner » doit rester FAUX : sur des effectifs de
        // 2 à 6 et des volumes proches de 1 m³, la somme brute rattrape parfois
        // la somme des produits.
        if (100 * (ns[0] + ns[1]) + vs[0] + vs[1] === T) continue;
        return { ns, vs, Ps, T };
    }
    return { ns: [3, 5], vs: [241, 176], Ps: [723, 880], T: 1603 };
};

const paramsSP = (contexte, intermediaires, expression, extra = {}) => ({
    niveau: 4,
    support: 'contexte',
    sansParenthese: true,
    operations: 3,
    contexte,
    intermediaires,
    expression,
    ...extra,
});

const genSommeProduitsQcm = () => {
    if (randomChoice([true, false])) {
        const t = tirerSP2();
        const { n1, p1, n2, p2, ctx } = t;
        const bonne = `${n1} × ${p1} + ${n2} × ${p2}`;
        const pieges = [
            { valeur: `${n1} × ${p2} + ${n2} × ${p1}`, code: 'grandeurs_confondues' },
            { valeur: `(${n1} + ${n2}) × (${p1} + ${p2})`, code: 'parenthese_systematique' },
            { valeur: `${n1} + ${p1} + ${n2} + ${p2}`, code: 'operation_confondue' },
        ];
        const texte = `<p>Des bénévoles collectent <strong>${n1} ${ctx.c1}</strong> contenant ${ctx.ch} ` +
            `<strong>${p1} ${ctx.o1}</strong> et <strong>${n2} ${ctx.c2}</strong> contenant ${ctx.ch} ` +
            `<strong>${p2} ${ctx.o2}</strong>.</p>` +
            `<p>Quelle expression permet de calculer le nombre total de ${ctx.quoi} ?</p>`;
        const payload = makeQcm([bonne, ...pieges.map((p) => p.valeur)], bonne, texte, opts(L_SP));
        return avecParams(payload, paramsSP(
            [n1, p1, n2, p2, t.P1, t.P2, t.T], [t.P1, t.P2], bonne,
            { distracteurs: pieges },
        ));
    }
    const t = tirerSP2dec();
    const [n1, n2] = t.ns;
    const [v1, v2] = t.vs.map(centToFr);
    const bonne = `${n1} × ${v1} + ${n2} × ${v2}`;
    const pieges = [
        { valeur: `${n1} × ${v2} + ${n2} × ${v1}`, code: 'grandeurs_confondues' },
        { valeur: `(${n1} + ${n2}) × (${v1} + ${v2})`, code: 'parenthese_systematique' },
        { valeur: `${n1} + ${v1} + ${n2} + ${v2}`, code: 'operation_confondue' },
    ];
    const texte = '<p>Pour restaurer une zone humide : ' +
        `<strong>${n1} zones</strong> reçoivent chacune <strong>${v1}${NB}m³</strong> de terre et ` +
        `<strong>${n2} zones</strong> reçoivent chacune <strong>${v2}${NB}m³</strong>.</p>` +
        '<p>Quelle expression permet de calculer le volume total de terre déplacée ?</p>';
    const payload = makeQcm([bonne, ...pieges.map((p) => p.valeur)], bonne, texte, opts(L_SP));
    return avecParams(payload, paramsSP(
        [n1, valeur(v1), n2, valeur(v2), t.Ps[0] / 100, t.Ps[1] / 100, t.T / 100],
        [t.Ps[0] / 100, t.Ps[1] / 100], bonne,
        { distracteurs: pieges },
    ));
};

const genSommeProduitsTrous = () => {
    if (randomChoice([true, false])) {
        const t = tirerSP2();
        const { n1, p1, n2, p2, P1, P2, T, ctx } = t;
        const texte = `<p>Des bénévoles collectent <strong>${n1} ${ctx.c1}</strong> contenant ${ctx.ch} ` +
            `<strong>${p1} ${ctx.o1}</strong> et <strong>${n2} ${ctx.c2}</strong> contenant ${ctx.ch} ` +
            `<strong>${p2} ${ctx.o2}</strong>. Combien de ${ctx.quoi} en tout ? Complète :</p>`;
        // Un produit par ligne : deux trous voisins autour d'un « + » seraient
        // permutables, l'élève qui les échange écrirait une égalité vraie comptée
        // fausse. Ici chaque trou est collé à SON produit (revue 2026-08-02).
        const phrase =
            `T = ${n1} × ${p1} + ${n2} × ${p2}\n` +
            `${n1} × ${p1} = {{${P1}}}\n` +
            `${n2} × ${p2} = {{${P2}}}\n` +
            `T = {{${T}}}`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_SP), mode: 'saisie' });
        return avecParams(payload, paramsSP(
            [n1, p1, n2, p2, P1, P2, T], [P1, P2],
            `${n1} × ${p1} + ${n2} × ${p2}`,
            { erreurVisee: 'resultat_recopie' },
        ));
    }
    const t = tirerSP2dec();
    const [n1, n2] = t.ns;
    const [v1, v2] = t.vs.map(centToFr);
    const [P1, P2] = t.Ps.map(centToFr);
    const texte = '<p>Pour restaurer une zone humide : ' +
        `<strong>${n1} zones</strong> reçoivent chacune <strong>${v1}${NB}m³</strong> de terre et ` +
        `<strong>${n2} zones</strong> reçoivent chacune <strong>${v2}${NB}m³</strong>. ` +
        'Quel volume total ? Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    const phrase =
        `V = ${n1} × ${v1} + ${n2} × ${v2}\n` +
        `${n1} × ${v1} = {{${P1}}}\n` +
        `${n2} × ${v2} = {{${P2}}}\n` +
        `V = {{${centToFr(t.T)}}}`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_SP), mode: 'saisie' });
    return avecParams(payload, paramsSP(
        [n1, valeur(v1), n2, valeur(v2), t.Ps[0] / 100, t.Ps[1] / 100, t.T / 100],
        [t.Ps[0] / 100, t.Ps[1] / 100],
        `${n1} × ${v1} + ${n2} × ${v2}`,
        { erreurVisee: 'resultat_recopie' },
    ));
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — Travail à rebours (niveau 4)
// ═══════════════════════════════════════════════════════════

const CTX_REB = [
    { a1: 'un cahier', a2: 'un classeur', a3: 'une trousse', du3: 'd\'une trousse', pl1: 'les cahiers', pl2: 'les classeurs' },
    { a1: 'un carnet', a2: 'un agenda', a3: 'un compas', du3: 'd\'un compas', pl1: 'les carnets', pl2: 'les agendas' },
];

/**
 * Le prix inconnu est tiré D'ABORD → toutes les étapes exactes à rebours.
 * Bornes : la dépense totale, qui est affichée dans l'énoncé, reste sous 400
 * (spec, nombres d'une situation en contexte) — d'où un effectif d'au plus 18
 * et des fournitures et non des manuels.
 */
const tirerRebours = () => {
    const n = randomChoice([14, 15, 16, 18]);
    const p1C = randInt(2, 4) * 100 + randomChoice([20, 40, 60, 80]);    // 2,20 € à 4,80 €
    // p2 = n rendrait le piège « (n + p1) × p2 » numériquement ÉGAL à la bonne
    // réponse n × (p1 + p2) : deux expressions justes, une comptée fausse.
    // L'égalité vaut exactement à cette condition, et n ≥ 14 > p2 : la garde
    // est structurelle (revue 2026-08-02, bornes revues 2026-09-22).
    const p2 = randInt(6, 9);                                            // entier
    let pfC = randomChoice([200, 600, 700, 800]);                        // prix inconnu (simple)
    while (pfC === p2 * 100) pfC = randomChoice([200, 600, 700, 800]);
    const dC = n * (p1C + p2 * 100);                                     // dépense connue
    const rC = n * pfC;                                                  // reste
    return { n, p1C, p2, pfC, dC, rC, tC: dC + rC, ctx: randomChoice(CTX_REB) };
};

const enonceRebours = ({ n, p1C, p2, tC, ctx }) =>
    `<p>Dans une classe de <strong>${n} élèves</strong>, chaque élève reçoit ` +
    `<strong>${ctx.a1} à ${euroFr(p1C)}${NB}€</strong>, <strong>${ctx.a2} à ${p2}${NB}€</strong> ` +
    `et <strong>${ctx.a3}</strong>. Le collège a dépensé en tout ` +
    `<strong>${euroFr(tC)}${NB}€</strong>.</p>`;

const paramsRebours = (t, expression, extra = {}) => ({
    niveau: 4,
    support: 'contexte',
    sansParenthese: false,
    operations: 2,
    contexte: [t.n, t.p1C / 100, t.p2, t.tC / 100, t.dC / 100, t.rC / 100, t.pfC / 100],
    intermediaires: [(t.p1C + t.p2 * 100) / 100],
    expression,
    ...extra,
});

const genReboursQcm = () => {
    const t = tirerRebours();
    const { n, p1C, p2, ctx } = t;
    const p1F = euroFr(p1C);
    const bonne = `${n} × (${p1F} + ${p2})`;
    const pieges = [
        // La parenthèse manque : le second prix n'est compté qu'une fois.
        { valeur: `${n} × ${p1F} + ${p2}`, code: 'parenthese_omise' },
        // La structure est inversée : l'effectif entre dans la parenthèse.
        { valeur: `(${n} + ${p1F}) × ${p2}`, code: 'ordre_inverse' },
        { valeur: `${n} + ${p1F} + ${p2}`, code: 'operation_confondue' },
    ];
    const texte = enonceRebours(t) +
        `<p>Quelle expression donne la dépense pour ${ctx.pl1} et ${ctx.pl2} ?</p>`;
    const payload = makeQcm([bonne, ...pieges.map((p) => p.valeur)], bonne, texte, opts(L_REB));
    return avecParams(payload, paramsRebours(t, bonne, { distracteurs: pieges }));
};

const genReboursTrous = () => {
    const t = tirerRebours();
    const { n, p1C, p2, pfC, dC, rC, ctx } = t;
    const texte = enonceRebours(t) +
        `<p>Quel est le prix ${ctx.du3} ? ` +
        'Complète (écris les montants avec deux décimales, ex. : 12,50) :</p>';
    const phrase =
        `Dépense pour ${ctx.pl1} et ${ctx.pl2} : ` +
        `${n} × (${euroFr(p1C)} + ${p2}) = {{${euroFr(dC)}}} €\n` +
        `Reste : ${euroFr(t.tC)} − {{${euroFr(dC)}}} = {{${euroFr(rC)}}} €\n` +
        `Prix ${ctx.du3} : {{${euroFr(rC)}}} ÷ ${n} = {{${euroFr(pfC)}}} €`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_REB), mode: 'saisie' });
    // Calculatrice AUTORISÉE (revue 2026-08-02) : la chaîne à rebours enchaîne
    // un produit décimal, une soustraction décimale puis une division — l'échec
    // doit porter sur le raisonnement, pas sur la division posée.
    return avecParams(avecCalculatrice(payload), paramsRebours(t, `${n} × (${euroFr(p1C)} + ${p2})`, {
        erreurVisee: 'resultat_recopie',
    }));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    succession_texte:                 genSuccessionSaisie,
    succession_qcm_unique:            genSuccessionQcm,
    programme_texte:                  genProgrammeSaisie,
    programme_qcm_unique:             genProgrammeQcm,
    programme_long_texte:             genProgrammeLongSaisie,
    lire_expression_qcm_unique:       genLireExpressionQcm,
    salaire_qcm_unique:               genSalaireQcm,
    salaire_texte:                    genSalaireSaisie,
    somme_produits_qcm_unique:        genSommeProduitsQcm,
    somme_produits_texte_trou_saisie: genSommeProduitsTrous,
    rebours_qcm_unique:               genReboursQcm,
    rebours_texte_trou_saisie:        genReboursTrous,
};

export const CATEGORIES = [
    {
        id: 'succession', label: L_SUCC,
        formatsList: [
            { key: 'succession_texte',      label: 'Saisie' },
            { key: 'succession_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'programme', label: L_PROG,
        formatsList: [
            { key: 'programme_texte',      label: 'Saisie' },
            { key: 'programme_qcm_unique', label: 'QCM' },
            { key: 'programme_long_texte', label: 'Saisie (4 étapes)' },
        ],
    },
    {
        id: 'lire_expression', label: L_LIRE,
        formatsList: [
            { key: 'lire_expression_qcm_unique', label: 'QCM' },
        ],
    },
    {
        id: 'salaire', label: L_SAL,
        formatsList: [
            { key: 'salaire_qcm_unique', label: 'QCM' },
            { key: 'salaire_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'somme_produits', label: L_SP,
        formatsList: [
            { key: 'somme_produits_qcm_unique',        label: 'QCM' },
            { key: 'somme_produits_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'rebours', label: L_REB,
        formatsList: [
            { key: 'rebours_qcm_unique',        label: 'QCM' },
            { key: 'rebours_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    centToFr, euroFr, assembler, ecrituresExpr,
    tirerSuccession, tirerProgramme, tirerSalaire, tirerSP2, tirerSP2dec, tirerRebours,
};
